import React, { useEffect, useRef, useState } from "react";
import Button from "../../../Common/FormElements/Button/Button";
import { useTranslation } from "react-i18next";
import { editComment } from "../../../../REDUX/actions/comment";
import { useDispatch } from "react-redux";
import RichTextEditor from "../../../RichTextEditor";
import { isEmptyHtml } from "../../../../Utils/richTextEditor";

function EditMessageInput({ message, close, actionType }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("common");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const contentEditableRef = useRef(null);
  const emojiRef = useRef(null);
  const selectionRef = useRef(null);
  const [updatedMessage, setUpdatedMessage] = useState({
    text: message?.attributes?.text,
    attachments: message?.attributes?.attachments?.data,
  });

  const handleFocus = () => {
    if (contentEditableRef?.current) {
      contentEditableRef.current.focus();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "attachments") {
      setUpdatedMessage({
        ...updatedMessage,
        [name]:
          updatedMessage?.attachments?.length > 0
            ? [...updatedMessage?.attachments, ...value]
            : value,
      });
    } else {
      setUpdatedMessage({ ...updatedMessage, [name]: value });
    }
  };

  const handleSubmit = async (e, successCallback) => {
    e.preventDefault();
    if (
      updatedMessage?.text?.trim()?.length > 0 ||
      updatedMessage?.attachments?.length > 0
    ) {
      setLoading(true);
      dispatch(
        editComment(
          message?.id,
          updatedMessage,
          message?.attributes?.attachments?.data,
          actionType,
          successCallback,
          () => setLoading(false)
        )
      );
    }
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      selectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    sel.removeAllRanges();
    if (selectionRef.current) {
      sel.addRange(selectionRef.current);
    }
  };

  function insertEmojiAtCursor(contentEditable, emojiHTML) {
    const sel = window.getSelection();

    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);

    if (!contentEditable.contains(range.startContainer)) return;

    const emojiElement = document.createElement("span");
    emojiElement.innerHTML = emojiHTML;

    const frag = document.createDocumentFragment();
    let node, lastNode;

    while ((node = emojiElement.firstChild)) {
      lastNode = frag.appendChild(node);
    }

    range.deleteContents();
    range.insertNode(frag);

    if (lastNode) {
      range.setStartAfter(lastNode);
      range.setEndAfter(lastNode);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    setUpdatedMessage((prev) => ({
      ...prev,
      text: contentEditable.innerHTML,
    }));

    saveSelection();
  }

  const onEmojiClick = (emojiObject) => {
    restoreSelection();
    contentEditableRef.current.focus();

    const img = `
      <img
        src="${emojiObject.imageUrl}"
        alt="${emojiObject.unified}"
        width={18}
        height={18}
        loading="${emojiObject.lazyLoad ? "lazy" : "eager"}"
        style="vertical-align: middle;"
      />
    `;
    insertEmojiAtCursor(contentEditableRef.current, img);
  };

  const toggleEmojiPicker = () => {
    if (!showEmojiPicker) {
      saveSelection();
    }
    setShowEmojiPicker((val) => !val);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    handleFocus();
  }, []);

  useEffect(() => {
    const textarea = contentEditableRef.current;

    const adjustTextareaHeight = () => {
      if (textarea) {
        textarea.style.height = "auto"; // Reset the height to auto to calculate the new height
        textarea.style.height = `${textarea.scrollHeight}px`; // Set the height to match the content's scrollHeight
      }
    };

    if (textarea) {
      textarea.addEventListener("input", adjustTextareaHeight);
      adjustTextareaHeight(); // Call it initially to set the correct height
    }

    return () => {
      if (textarea) {
        textarea.removeEventListener("input", adjustTextareaHeight);
      }
    };
  }, []);

  return (
    <div className="formContainer position-sticky w100 z-9999">
      <RichTextEditor
        edit
        loading={loading}
        handleChange={handleChange}
        newMessage={updatedMessage}
        setNewMessage={setUpdatedMessage}
        handleSubmit={handleSubmit}
      />
      <div className="d-flex align-content-center justify-content-end mt-2 w-100">
        <Button className="sendBtn ms-2 mt-auto" onClick={close}>
          {t("Cancel")}
        </Button>
        <Button
          className="sendBtn ms-2 mt-auto"
          loading={loading}
          isSubmit={false}
          onClick={(e) => {
            handleSubmit(e, () => close());
          }}
          disabled={
            (isEmptyHtml(updatedMessage.text) &&
              !updatedMessage?.attachments?.length > 0) ||
            loading
          }
        >
          {t("Save")}
        </Button>
      </div>
    </div>
  );
}

export default EditMessageInput;
